import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { WasteSavingsWidget, type WasteSavingsResponse } from '../components/WasteSavingsWidget.js';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SavingsPage() {
  const { data, isLoading } = useQuery<WasteSavingsResponse>({
    queryKey: ['waste-savings'],
    queryFn: () => api.get('/api/me/waste-savings'),
    staleTime: 5 * 60_000,
  });

  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 960, padding: '32px 24px 56px' }}>

        <div className="mb-8">
          <Link to="/dashboard" className="text-sm font-medium inline-flex items-center gap-1 mb-3 hover:opacity-80"
            style={{ color: 'var(--ink-3)' }}>
            <Icon name="chev" size={13} style={{ transform: 'rotate(180deg)' }} /> Back to dashboard
          </Link>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="leaf" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Food waste saved
          </div>
          <h1 className="h-display" style={{ fontSize: 'clamp(36px, 5vw, 52px)', margin: 0, lineHeight: 1.05, color: 'var(--ink)' }}>
            Your full breakdown
          </h1>
        </div>

        {/* Hero stat */}
        {isLoading ? (
          <div className="card p-6 mb-8">
            <div className="animate-pulse h-12 w-48 rounded" style={{ background: 'var(--surface-2)' }} />
          </div>
        ) : data && data.totalSavedLb > 0 ? (
          <div className="card p-6 mb-8">
            <div className="text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'var(--ink-3)', letterSpacing: '0.14em' }}>Total saved</div>
            <div className="h-display" style={{ fontSize: 64, lineHeight: 1, color: 'var(--accent)' }}>
              {data.totalSavedLb} lb
            </div>
            <div className="text-sm mt-2" style={{ color: 'var(--ink-3)' }}>
              across {data.cookedPlanCount} cooked plan{data.cookedPlanCount === 1 ? '' : 's'}
              {' · '}
              {data.perPlan.length} cooked event{data.perPlan.length === 1 ? '' : 's'} total
            </div>
          </div>
        ) : (
          <div className="card p-6 mb-8">
            <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
              No cooked plans yet. Once you start cooking, your savings will show up here.
            </p>
          </div>
        )}

        {/* Per-plan list */}
        {data && data.perPlan.length > 0 && (
          <section className="mb-8">
            <h2 className="h-serif" style={{ fontSize: 22, margin: 0, color: 'var(--ink)', marginBottom: 16 }}>
              Per plan
            </h2>
            <div className="card p-6">
              <div className="flex flex-col divide-y" style={{ borderColor: 'var(--line-soft)' }}>
                {data.perPlan.map((plan, idx) => (
                  <div key={`${plan.mealPlanId ?? 'unplanned'}-${idx}`} className="py-3 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold uppercase tracking-wider mb-1"
                        style={{ color: 'var(--ink-4)', letterSpacing: '0.1em' }}>
                        {formatDate(plan.cookedAt)}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--ink-2)', lineHeight: 1.4 }}>
                        {plan.recipeNames.length > 0 ? plan.recipeNames.join(', ') : '—'}
                      </div>
                    </div>
                    <div className="text-sm font-semibold flex-shrink-0 tabular-nums" style={{ color: 'var(--accent)' }}>
                      {plan.savedG}g
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Full ingredient chart */}
        <section className="mb-8">
          <h2 className="h-serif" style={{ fontSize: 22, margin: 0, color: 'var(--ink)', marginBottom: 16 }}>
            Per ingredient
          </h2>
          <WasteSavingsWidget showAll />
        </section>

        {/* Methodology */}
        <section className="card p-6">
          <h2 className="h-serif" style={{ fontSize: 18, margin: 0, color: 'var(--ink)', marginBottom: 8 }}>
            How we calculate this
          </h2>
          <p className="text-sm mb-3" style={{ color: 'var(--ink-3)', lineHeight: 1.6 }}>
            Savings are derived from USDA Loss-Adjusted Food Availability data — the
            same conservative household-loss percentages used in federal nutrition
            reporting. For each ingredient in a recipe you mark as cooked, we credit
            the typical waste avoided versus an ad-hoc shopping trip.
          </p>
          <Link to="/methodology#waste-savings" className="text-sm font-semibold inline-flex items-center gap-1 hover:opacity-80"
            style={{ color: 'var(--accent)' }}>
            Read the full methodology <Icon name="chev" size={13} />
          </Link>
        </section>
      </div>
    </Layout>
  );
}
